/* Maison wishlist — localStorage-backed hearts + drawer.
   No app dependency; storage key is versioned so a later wishlist app
   migration can read and retire it. */
(() => {
  'use strict';

  const STORAGE_KEY = 'maison:wishlist:v1';

  const readItems = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  const writeItems = (items) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      /* private mode etc. — hearts still work for the session via memory */
      memoryItems = items;
    }
    broadcast();
  };

  let memoryItems = null;
  const items = () => (memoryItems !== null ? memoryItems : readItems());

  const hasItem = (handle) => items().some((item) => item.handle === handle);

  const toggleItem = (item) => {
    const list = items().slice();
    const index = list.findIndex((existing) => existing.handle === item.handle);
    const added = index < 0;
    if (!added) {
      list.splice(index, 1);
    } else {
      list.push({ ...item, savedAt: new Date().toISOString() });
    }
    if (memoryItems !== null) memoryItems = list;
    writeItems(list);
    return added;
  };

  const removeItem = (handle) => {
    const list = items().filter((item) => item.handle !== handle);
    if (memoryItems !== null) memoryItems = list;
    writeItems(list);
  };

  const itemFromTrigger = (trigger) => ({
    handle: trigger.dataset.wlHandle,
    title: trigger.dataset.wlTitle || '',
    url: trigger.dataset.wlUrl || '#',
    price: trigger.dataset.wlPrice || '',
    compare: trigger.dataset.wlCompare || '',
    image: trigger.dataset.wlImage || '',
  });

  /* ---------- UI sync ---------- */

  const syncHearts = () => {
    document.querySelectorAll('[data-maison-wishlist-toggle]').forEach((trigger) => {
      const saved = hasItem(trigger.dataset.wlHandle);
      trigger.classList.toggle('is-saved', saved);
      trigger.setAttribute('aria-pressed', saved ? 'true' : 'false');
    });
  };

  const syncCounts = () => {
    const count = items().length;
    document.querySelectorAll('[data-maison-wishlist-count]').forEach((bubble) => {
      bubble.textContent = count > 99 ? '99+' : String(count);
      bubble.hidden = count === 0;
    });
  };

  /* ---------- drawer ---------- */

  let drawer = null;
  let scrim = null;
  let lastFocus = null;

  const heartSvg =
    '<svg class="maison-icon maison-icon--heart" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 20C12 20 4 14.5 4 9C4 6.5 6 5 8 5C9.7 5 11.2 6 12 7.4C12.8 6 14.3 5 16 5C18 5 20 6.5 20 9C20 14.5 12 20 12 20Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>';

  const buildDrawer = () => {
    if (drawer) return;

    scrim = document.createElement('div');
    scrim.className = 'maison-wishlist-scrim';
    scrim.addEventListener('click', closeDrawer);

    drawer = document.createElement('aside');
    drawer.className = 'maison-wishlist-drawer';
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-label', 'Saved pieces');
    drawer.innerHTML =
      '<div class="maison-wishlist-drawer__head">' +
      '<span class="maison-wishlist-drawer__label" data-maison-wishlist-heading>SAVED</span>' +
      '<button type="button" class="maison-wishlist-drawer__close" data-maison-wishlist-close aria-label="Close">' +
      '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>' +
      '</button></div>' +
      '<div class="maison-wishlist-drawer__body" data-maison-wishlist-list></div>';
    drawer.addEventListener('click', (event) => {
      const closer = event.target.closest('[data-maison-wishlist-close]');
      if (closer) closeDrawer();
      const remover = event.target.closest('[data-maison-wishlist-remove]');
      if (remover) {
        removeItem(remover.dataset.maisonWishlistRemove);
        renderDrawer();
      }
    });

    document.body.appendChild(scrim);
    document.body.appendChild(drawer);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });
  };

  const renderDrawer = () => {
    if (!drawer) return;
    const list = items();
    const heading = drawer.querySelector('[data-maison-wishlist-heading]');
    heading.textContent = list.length ? 'SAVED · ' + String(list.length).padStart(2, '0') : 'SAVED';

    const body = drawer.querySelector('[data-maison-wishlist-list]');
    if (!list.length) {
      body.innerHTML =
        '<div class="maison-wishlist-drawer__empty">' + heartSvg +
        '<p>NOTHING SAVED YET</p>' +
        '<a class="maison-wishlist-drawer__shop" href="/collections/all">EXPLORE THE COLLECTION</a></div>';
      return;
    }

    body.innerHTML = list
      .map((item) => {
        const media = item.image
          ? '<img src="' + item.image + '" alt="" loading="lazy">'
          : '<span class="maison-wishlist-drawer__noimg" aria-hidden="true"></span>';
        const compare = item.compare ? '<s>' + item.compare + '</s> ' : '';
        return (
          '<div class="maison-wishlist-drawer__item">' +
          '<a class="maison-wishlist-drawer__media" href="' + item.url + '">' + media + '</a>' +
          '<div class="maison-wishlist-drawer__info">' +
          '<a href="' + item.url + '">' + item.title + '</a>' +
          '<span class="maison-wishlist-drawer__price">' + compare + (item.price || '') + '</span>' +
          '</div>' +
          '<button type="button" class="maison-wishlist-drawer__remove" data-maison-wishlist-remove="' + item.handle + '" aria-label="Remove ' + item.title + '">' +
          '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>' +
          '</button></div>'
        );
      })
      .join('');
  };

  const openDrawer = () => {
    buildDrawer();
    renderDrawer();
    lastFocus = document.activeElement;
    document.body.classList.add('maison-wishlist-open');

    /* The drawer is created lazily. Flush its closed position before opening
       so the first open animates just like every later open. */
    drawer.getBoundingClientRect();
    scrim.getBoundingClientRect();
    drawer.classList.add('is-open');
    scrim.classList.add('is-open');

    const close = drawer.querySelector('[data-maison-wishlist-close]');
    if (close) close.focus({ preventScroll: true });
  };

  const closeDrawer = () => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    scrim.classList.remove('is-open');
    document.body.classList.remove('maison-wishlist-open');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
  };

  /* ---------- events ---------- */

  const broadcast = () => {
    syncHearts();
    syncCounts();
    if (drawer && drawer.classList.contains('is-open')) renderDrawer();
    document.dispatchEvent(new CustomEvent('maison:wishlist:change', { detail: { count: items().length } }));
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-maison-wishlist-toggle]');
    if (toggle && toggle.dataset.wlHandle) {
      event.preventDefault();
      const added = toggleItem(itemFromTrigger(toggle));
      if (added) openDrawer();
      return;
    }
    const opener = event.target.closest('[data-maison-wishlist-open]');
    if (opener) {
      event.preventDefault();
      openDrawer();
    }
  });

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) broadcast();
  });

  document.addEventListener('shopify:section:load', () => {
    syncHearts();
    syncCounts();
  });

  document.addEventListener('maison:grid:append', () => {
    syncHearts();
  });

  const initialise = () => {
    syncHearts();
    syncCounts();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
